import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from './I18nContext';

/* ============================================================
   Impact — Editorial Testimonials + 3 Sovereign Stats
============================================================ */

interface Stat {
  value: string;
  numeric: number;
  suffix: string;
  label: string;
  sub: string;
  code: string;
}

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  initial: string;
}

const STATS_DATA = {
  de: [
    { value: '100%', numeric: 100, suffix: '%', label: 'EU-Datensouveränität', sub: 'Kein Bit verlässt das EU-Territorium. Garantiert.', code: 'M-01' },
    { value: '0', numeric: 0, suffix: '', label: 'Datenlecks', sub: 'Betriebshistorie seit 2022. Air-gapped Infrastructure.', code: 'M-02' },
    { value: '99.97%', numeric: 99.97, suffix: '%', label: 'SLA Uptime', sub: 'Redundante Power- und Netzwerkpfade mit Auto-Failover.', code: 'M-03' },
  ],
  it: [
    { value: '100%', numeric: 100, suffix: '%', label: 'Sovranità Dati UE', sub: 'Nessun bit lascia il territorio UE. Garantito.', code: 'M-01' },
    { value: '0', numeric: 0, suffix: '', label: 'Violazioni dati', sub: 'Storico operativo dal 2022. Infrastruttura air-gapped.', code: 'M-02' },
    { value: '99.97%', numeric: 99.97, suffix: '%', label: 'SLA Uptime', sub: 'Percorsi di alimentazione e rete ridondanti con Auto-Failover.', code: 'M-03' },
  ],
  en: [
    { value: '100%', numeric: 100, suffix: '%', label: 'EU Data Sovereignty', sub: 'Not a single bit leaves EU territory. Guaranteed.', code: 'M-01' },
    { value: '0', numeric: 0, suffix: '', label: 'Data Breaches', sub: 'Operational history since 2022. Air-gapped infrastructure.', code: 'M-02' },
    { value: '99.97%', numeric: 99.97, suffix: '%', label: 'SLA Uptime', sub: 'Redundant power and network paths with auto-failover.', code: 'M-03' },
  ],
};

const TESTIMONIALS_DATA = {
  de: [
    { quote: 'HenselX hat unsere gesamte Infrastruktur modernisiert. Endlich nutzen wir KI ohne Angst um unsere Firmendaten.', name: 'Marco Rossi', role: 'CTO, Milano Tech Solutions', initial: 'M' },
    { quote: 'Die Kombination aus Hardware-Wissen und Web-Entwicklung ist einzigartig. Performant, sicher — und zu einem fairen Preis.', name: 'Giulia Bianchi', role: 'CEO, Innovazione Calabria', initial: 'G' },
  ],
  it: [
    { quote: "HenselX ha modernizzato tutta la nostra infrastruttura. Finalmente usiamo l'AI senza preoccuparci dei dati aziendali.", name: 'Marco Rossi', role: 'CTO, Milano Tech Solutions', initial: 'M' },
    { quote: 'La combinazione di competenze hardware e sviluppo web è unica. Performante, sicuro — e a un prezzo equo.', name: 'Giulia Bianchi', role: 'CEO, Innovazione Calabria', initial: 'G' },
  ],
  en: [
    { quote: 'HenselX modernised our entire infrastructure. We finally use AI without worrying about our company data.', name: 'Marco Rossi', role: 'CTO, Milano Tech Solutions', initial: 'M' },
    { quote: 'The combination of hardware knowledge and web development is unique. Performant, secure — and at a fair price.', name: 'Giulia Bianchi', role: 'CEO, Innovazione Calabria', initial: 'G' },
  ],
};

function useCounter(target: number, decimals: number, active: boolean) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const start = Date.now();
    const duration = 1400;
    const raf = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(parseFloat((target * eased).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }, [active, target, decimals]);
  return val;
}

const StatCell: React.FC<{ stat: Stat; active: boolean; delay: number }> = ({ stat, active, delay }) => {
  const decimals = stat.value.includes('.') ? 2 : 0;
  const animated = useCounter(stat.numeric, decimals, active);
  const display = stat.numeric === 0 ? '0' : `${animated.toFixed(decimals)}${stat.suffix}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
      className="relative border border-slate-200 dark:border-white/7 rounded-xl bg-slate-50 dark:bg-[#080f1f] p-7 overflow-hidden group"
    >
      <span className="font-mono text-[9px] tracking-widest text-slate-500 uppercase mb-4 block">
        {stat.code}
      </span>
      <div className="font-mono text-5xl md:text-6xl font-bold text-slate-900 dark:text-white leading-none mb-2 hx-text-glow tabular-nums">
        {display}
      </div>
      <div className="text-sky-600 dark:text-sky-400 text-sm font-semibold mb-2">{stat.label}</div>
      <p className="text-slate-500 text-xs leading-relaxed">{stat.sub}</p>
      <div className="absolute inset-0 bg-sky-500/0 group-hover:bg-sky-500/[0.03] transition-colors duration-500 rounded-xl pointer-events-none" />
    </motion.div>
  );
};

export const Impact: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const { t, language } = useTranslation();
  const STATS = STATS_DATA[language as keyof typeof STATS_DATA] ?? STATS_DATA.de;
  const TESTIMONIALS = TESTIMONIALS_DATA[language as keyof typeof TESTIMONIALS_DATA] ?? TESTIMONIALS_DATA.de;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.25 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-28 px-6 bg-slate-50 dark:bg-[#080f1f] overflow-hidden"
    >
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-sky-500/5 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">

        <div className="mb-16">
          <span className="hx-badge mb-4 inline-block">{t('impact.badge')}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white leading-tight max-w-xl">
            {t('impact.title')}<br />
            <span className="text-sky-500 dark:text-sky-400">{t('impact.titleHighlight')}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Stats */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STATS.map((stat, i) => (
              <StatCell key={stat.code} stat={stat} active={active} delay={i * 0.12} />
            ))}
          </div>

          {/* Testimonials */}
          <div className="lg:col-span-2 flex flex-col gap-4 lg:mt-8">
            {TESTIMONIALS.map((testimonial, i) => (
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, x: 20 }}
                animate={active ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.55, delay: 0.25 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="relative bg-white dark:bg-[#020617] border border-slate-200 dark:border-white/7 rounded-xl p-6 overflow-hidden"
              >
                <span className="absolute -top-2 -left-1 text-sky-500/20 text-7xl font-serif leading-none select-none pointer-events-none">
                  "
                </span>
                <p className="relative text-slate-600 dark:text-slate-300 text-sm leading-relaxed italic mb-5">
                  {testimonial.quote}
                </p>
                <footer className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
                    <span className="font-mono text-sky-600 dark:text-sky-400 text-xs font-bold">{testimonial.initial}</span>
                  </div>
                  <div>
                    <cite className="not-italic text-slate-900 dark:text-white text-sm font-semibold block">{testimonial.name}</cite>
                    <span className="text-slate-500 text-xs">{testimonial.role}</span>
                  </div>
                </footer>
              </motion.blockquote>
            ))}

            {/* Hosted in Acri badge */}
            <div className="rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-[#020617]/50 p-5 flex items-center gap-4">
              <img
                src="/Grafx/henselx_hosted_in_acri.png"
                alt="Hosted in Acri – Natura & Comunità"
                className="h-16 w-auto rounded-lg opacity-90"
              />
              <div>
                <p className="text-slate-900 dark:text-white text-sm font-semibold mb-0.5">{t('impact.hosted')}</p>
                <p className="text-slate-500 text-xs leading-relaxed">{t('impact.hostedSub')}</p>
              </div>
            </div>
          </div>
        </div>

        <hr className="hx-divider mt-16" />
      </div>
    </section>
  );
};
